/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 *
 * @author IshakaW
 * 01/24/2022
 *
 */

import React from 'react';
import '../../form-elements/formElement.css';
import API from "../../../../../utils/api";
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import FormElementGenerator from "../../form-elements/formElementGenerator";
import Loader from "../../../loader/loader";

/**
 * HOC for the pearsonRoles form
 */
class PearsonRoleForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            // main pearson role fields
            pearsonRoleId: null,
            pearsonRoleName: '',
            isDisplayLoader: true,
            // error message container
            errors: {},
            // user token
            token: '',
            // form fields
            fields: [
                {
                    inputType: 'text',
                    labelName: 'Name *',
                    inputName: 'pearsonRoleName',
                    placeholder: 'Please enter pearson role name here',
                    required: true,
                    disabled: false
                }
            ]
        };

        // get the parameter from URL
        const pearsonRoleId = this.props.match.params.id;

        // set icons to the header
        if (pearsonRoleId === undefined) {
            EventEmitter.emit('setPageHeaderData', {
                page: 'pearson-roles',
                pageName: "Manage Pearson Roles",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/pearson-roles",
                optionalIcons: ['backIcon','clearFormIcon']
            });
        } else {
            EventEmitter.emit('setPageHeaderData', {
                page: 'pearson-roles',
                pageName: "Manage Pearson Roles",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/pearson-roles",
                optionalIcons: ['backIcon','clearFormIcon','deleteIcon']
            });
        }

        EventEmitter.on('saveFormData', this.handleSubmit);
        EventEmitter.on('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.on('deleteFormData', this.deletePearsonRole);
        EventEmitter.on('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.on('clearForm', this.clearForm);
    }

    componentDidMount = async () => {
        // get parameter from URL
        const pearsonRoleId = this.props.match.params.id;

        // get token
        const user = JSON.parse(localStorage.getItem('tmcUser'));
        const token = 'Bearer ' + user.token;

        const oldState = Object.assign({}, this.state);

        // fill the form if not a create pearson role
        if (pearsonRoleId !== undefined) {
            const url = '/pearson-roles?search=%7BpearsonRoleId:' + pearsonRoleId + '%7D';

            await API.get(url, {headers: {Authorization: token}}).then(res => {
                const data = res.data[0];
                oldState.pearsonRoleId = (data.pearsonRoleId !== undefined) ? data.pearsonRoleId : null;               
                oldState.pearsonRoleName = (data.pearsonRoleName !== undefined) ? data.pearsonRoleName : '';                
                oldState.token = token;
                oldState.isDisplayLoader = false;

                this.setState(oldState);
            }).catch(er => {
                oldState.isDisplayLoader = false;
                this.setState(oldState);
                this.displayErrorsForAPICalls(er);
            });
        } else {
            oldState.token = token;
            oldState.isDisplayLoader = false;
            this.setState(oldState);
        }
    };

    componentWillUnmount = () => {
        EventEmitter.off('setPageHeaderData', {});
        EventEmitter.off('saveFormData', this.handleSubmit);
        EventEmitter.off('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.off('deleteFormData', this.deletePearsonRole);
        EventEmitter.off('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.off('clearForm', this.clearForm);
    };

    /**
     * set the state
     * @param event
     */
    handleChange = event => {
        if (event.optionValueName !== undefined) {
            this.setState({
                [event.optionValueName]: event.value,
                [event.optionLabelName]: event.label
            }, () => {
                this.validate(event.optionValueName, event.value)
            });
        } else {
            const key = event.currentTarget.name;
            const val = event.currentTarget.value;

            this.setState({
                [key]: val
            }, () => {
                this.validate(key, val)
            });
        }
    };

    handleSubmit = () => {
        // data to be sent
        let data = null;
        // check its a update or create
        if (this.state.pearsonRoleId !== null) {
            data = {
                pearsonRoleId: this.state.pearsonRoleId,                
                pearsonRoleName: this.state.pearsonRoleName                
            };
        } else {
            data = {
                pearsonRoleName: this.state.pearsonRoleName
            };
        }

        // validate each form field
        for (let [key, value] of Object.entries(data)) {
            this.validate(key, value);
        }

        // check weather there are error messages
        if (Object.values(this.state.errors).find(o => o !== '') === undefined) {
            // check update or create
            if (this.state.pearsonRoleId !== null) {
                this.updatePearsonRole(data);
            } else {
                this.createPearsonRole(data);
            }
        }
    };

    validate = (key, value) => {
        let error = {};

        if (key === 'pearsonRoleName')
            if (value === '')
                error[key] = "Pearson Role Name is Required!";
            else if (value === ' ')
                error[key] = "Pearson Role Name can't be an empty space!";
            else if ((new TextEncoder().encode(value).length > 32))
                error[key] = "Pearson Role Name can't have more than 32 characters!";
            else
                error[key] = "";

        const newState = Object.assign({}, this.state);
        newState.errors = Object.assign(newState.errors, error);

        this.setState(newState);
    };

    createPearsonRole = async (data) => {
        await API.post('/pearson-roles',
            {
                "pearsonRoleName": data.pearsonRoleName
            }, {headers: {Authorization: this.state.token}}
        ).then(res => {
            this.setAlert("Success", "Success", "Successfully Created the pearson role");
            this.props.history.push('/home/pearson-roles');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    updatePearsonRole = async (data) => {
        await API.put('/pearson-roles',
            {
                "pearsonRoleId": data.pearsonRoleId,
                "pearsonRoleName": data.pearsonRoleName                
            },{headers: {Authorization: this.state.token}}
        ).then(response => {
            this.setAlert("Success", "Success", "Successfully updated the pearson role");
            this.props.history.push('/home/pearson-roles');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    displayDeleteWarningMessage = () => {
        if (this.state.pearsonRoleId !== null) {
            EventEmitter.emit('warningModelData', {
                type: 'pearson-roles',
                name: this.state.pearsonRoleName
            });
            EventEmitter.emit('openWarningModel', true);
        } else {
            EventEmitter.emit('setAlert', {
                openAlert: true,
                type: "Error",
                isDisplayAlertIcon: true,
                alertMessage: "You are trying to delete before loading the pearson role",
                alertTitle: "Error"
            });
        }

    };

    deletePearsonRole = async (data) => {
        if (data && (this.props.match.path === "/home/pearson-roles/:id")) {
            await API.delete('/pearson-roles?pearsonRoleId=' + this.state.pearsonRoleId, {headers: {Authorization: this.state.token}})
                .then(res => {
                    this.setAlert("Success", "Success", "Successfully Deleted the pearson role");
                    this.props.history.push('/home/pearson-roles');
                }).catch(er => {
                    this.displayErrorsForAPICalls(er);
                });
        }
    };

    displayErrorsForAPICalls = (error) => {
        if (error.response !== undefined && error.response.status === 404) {
            this.setAlert("Error", "Error", "The selected item not found!");
        } else if (error.message.includes('422')) {
            this.setAlert("Error", "Sorry", "Pearson Role is already exist!");
        } else if (error.response === undefined || error.response.status === 401) {
            localStorage.removeItem('tmcUser')
            this.props.history.push('/');
        } else {
            this.setAlert("Error", "Error", "Something went wrong");
        }
    };

    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    clearForm = () => {
        const newState = Object.assign({}, this.state);

        newState.pearsonRoleName = "";       
        newState.errors = {};

        this.setState(newState);
    };

    navigateToPreviousPage = () => {
        this.props.history.push('/home/pearson-roles');
    };

    render() {
        return(
            <div role="form" className="container">
                {this.state.isDisplayLoader && <Loader/>}
                <div className="pearsonrole-btn-container">
                    <div className="buttons" tooltip="Save" onClick={this.handleSubmit}>
                        <i className="fa fa-save fa-lg icon-primary" />
                    </div>
                </div>
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
            </div>
        )
    }
}

export default PearsonRoleForm;
